import { ArrowUpRight, ArrowDownRight, Loader2 } from "lucide-react";
import { formatINR } from "@/lib/format";

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 animate-fade-up">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="text-muted-foreground mt-1.5 text-sm md:text-base">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

export function KpiCard({ label, value, delta, deltaLabel, icon: Icon, accent = "primary", testid, index = 0 }) {
  const positive = delta >= 0;
  const accentMap = {
    primary: "text-primary bg-primary/10",
    success: "text-[hsl(var(--success))] bg-[hsl(var(--success))]/10",
    warning: "text-[hsl(var(--warning))] bg-[hsl(var(--warning))]/10",
    danger: "text-destructive bg-destructive/10",
  };
  return (
    <div
      data-testid={testid}
      className="group rounded-xl border border-border bg-card p-5 shadow-sm transition-transform duration-200 hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg animate-fade-up"
      style={{ animationDelay: `${index * 45}ms` }}
    >
      <div className="flex items-start justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
        {Icon && (
          <span className={`grid place-items-center h-9 w-9 rounded-lg ${accentMap[accent]}`}>
            <Icon className="h-[18px] w-[18px]" />
          </span>
        )}
      </div>
      <div className="mt-3 font-heading text-2xl md:text-[1.7rem] font-bold tracking-tight tabular-nums">{value}</div>
      {delta !== undefined && delta !== null && (
        <div className="mt-2 flex items-center gap-1.5 text-xs">
          <span className={`inline-flex items-center gap-0.5 font-semibold ${positive ? "text-[hsl(var(--success))]" : "text-destructive"}`}>
            {positive ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
            {Math.abs(delta)}%
          </span>
          <span className="text-muted-foreground">{deltaLabel || "vs last month"}</span>
        </div>
      )}
    </div>
  );
}

export function SectionCard({ title, subtitle, children, className = "", action, testid }) {
  return (
    <div data-testid={testid} className={`rounded-xl border border-border bg-card p-5 md:p-6 shadow-sm animate-fade-up ${className}`}>
      {(title || action) && (
        <div className="flex items-center justify-between mb-5">
          <div>
            {title && <h3 className="font-heading text-lg font-semibold tracking-tight">{title}</h3>}
            {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

export function Money({ value, compact = false, className = "" }) {
  return <span className={`tabular-nums ${className}`}>{formatINR(value, { compact })}</span>;
}

export function Loader({ label = "Loading..." }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-muted-foreground">
      <Loader2 className="h-7 w-7 animate-spin text-primary" />
      <p className="mt-3 text-sm">{label}</p>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      {Icon && <Icon className="h-10 w-10 text-muted-foreground/50" />}
      <p className="mt-3 font-medium">{title}</p>
      {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
    </div>
  );
}
